<template>
  <div class="account">
    <div class="account__tabs">
      <ul class="account__tabs__index">
        <li
          v-for="tab in tabsActive"
          :key="tab.key"
          class="account__tabs__index__tab"
          :class="{ 'account__tabs__index__tab--on': tabSelected === tab.key }"
        >
          <a
            :href="`#${tab.key}`"
            @click.prevent="selectTab(tab.key)"
          >
            {{ tab.label }}
          </a>
        </li>
      </ul>

      <!-- tabUserData -->
      <div
        v-if="tabSelected === 'tabUserData'"
        id="tabUserData"
        class="account__tabs__content"
      >
        <BaseGrid columns="2">
          <form
            class="account__form"
            :class="{ 'account__form--disabled': isLoading }"
            @submit.prevent="submitUserData"
          >
            <Transition name="transition-fade">
              <div
                v-if="alert.userData"
                class="alert"
                :class="`alert--${alert.userData.type}`"
                v-html="alert.userData.message"
              />
            </Transition>

            <div class="form-field">
              <label class="form-field__label">
                Nome

                <BaseInputText
                  id="inputName"
                  v-model="form.userData.name"
                  readonly
                />
              </label>
            </div>

            <div class="form-field">
              <label class="form-field__label">
                E-mail

                <BaseInputText
                  id="inputEmail"
                  type="email"
                  v-model="form.userData.email"
                  readonly
                />
              </label>
            </div>

            <div class="form-field">
              <label class="form-field__label">
                Celular

                <BaseInputText
                  id="inputPhone"
                  mask="(##) #####-####"
                  v-model="form.userData.phone"
                  required
                />
              </label>
            </div>

            <div class="form-action">
              <BaseButton
                :isLoading="isLoading"
                type="submit"
              >
                Salvar
              </BaseButton>
            </div>
          </form>
        </BaseGrid>
      </div>

      <!-- tabPassword -->
      <div
        v-if="tabSelected === 'tabPassword'"
        id="tabPassword"
        class="account__tabs__content"
      >
        <BaseGrid columns="2">
          <form
            class="account__form"
            :class="{ 'account__form--disabled': isLoading }"
            @submit.prevent="submitPassword"
          >
            <Transition name="transition-fade">
              <div
                v-if="alert.password"
                class="alert"
                :class="`alert--${alert.password.type}`"
                v-html="alert.password.message"
              />
            </Transition>

            <div class="form-field">
              <label class="form-field__label">
                Senha atual

                <BaseInputText
                  id="inputPasswordCurrent"
                  type="password"
                  v-model="form.password.current"
                  required
                />
              </label>
            </div>

            <div class="form-field">
              <label class="form-field__label">
                Nova senha

                <BaseInputText
                  id="inputPasswordNew"
                  type="password"
                  v-model="form.password.new"
                  required
                />
              </label>
            </div>

            <div class="form-field">
              <label class="form-field__label">
                Confirmar senha

                <BaseInputText
                  id="inputPasswordNewConfirm"
                  type="password"
                  v-model="form.password.newConfirm"
                  required
                />
              </label>
            </div>

            <div
              class="error-message"
            >
              {{ passwordFormState.message || '&nbsp;' }}
            </div>

            <div class="form-action">
              <BaseButton
                :isLoading="isLoading"
                type="submit"
              >
                Salvar
              </BaseButton>
            </div>
          </form>
        </BaseGrid>
      </div>
    </div>
  </div>
</template>

<script>
import BaseGrid from '@/components/base/BaseGrid'
import BaseInputText from '@/components/base/BaseInputText'
import BaseButton from '@/components/base/BaseButton'
import { mapState, mapGetters } from 'vuex'
import axios from 'axios'

export default {
  name: 'WidgetUserData',

  components: {
    BaseGrid,
    BaseInputText,
    BaseButton
  },

  data () {
    return {
      isLoading: false,
      tabs: [
        {
          key: 'tabUserData',
          label: 'Meus dados',
          isActive: true
        },
        {
          key: 'tabPassword',
          label: 'Alterar senha',
          isActive: true
        }
      ],
      tabSelected: 'tabUserData',
      form: {
        userData: {
          name: '',
          email: '',
          phone: ''
        },
        password: {
          current: '',
          new: '',
          newConfirm: ''
        }
      },
      passwordFormState: {
        message: '',
        state: false
      },
      alert: {
        userData: null,
        password: null
      },
      errorMessages: {
        phone: 'Verifique o campo <strong>Celular</strong> e tente novamente.',
        WRONG_PASSWORD: 'Verifique a <strong>Senha atual</strong> e tente novamente.',
        NO_MATCHING_PASSWORDS: 'Verifique a <strong>Nova senha</strong> e tente novamente.'
      }
    }
  },

  computed: {
    ...mapState('base', ['mainApi']),

    ...mapGetters('auth', [
      'userId',
      'platformId'
    ]),

    tabsActive () {
      return this.tabs.filter(tab => tab.isActive === true)
    }
  },

  created () {
    this.fetchUserData()
  },

  methods: {
    selectTab (key) {
      if (this.tabSelected === key) {
        return
      }

      this.tabSelected = key
    },

    async fetchUserData () {
      this.isLoading = true

      try {
        const url = `${this.mainApi}auth/user/user-my-data`
        const response = await axios.get(url)
        const { name, email, phone } = response.data

        Object.assign(this.form.userData, {
          name: name || '',
          email: email || '',
          phone: phone || ''
        })
      } catch (error) {
        throw error
      } finally {
        this.isLoading = false
      }
    },

    async submitUserData () {
      this.isLoading = true
      this.alert.userData = null

      try {
        const url = `${this.mainApi}auth/user/user-my-data`
        const payload = {
          userId: this.userId,
          ...this.form.userData
        }
        const response = await axios.post(url, payload)

        this.alert.userData = {
          type: 'success',
          message: 'Dados atualizados com sucesso!'
        }
      } catch (error) {
        const { fieldName: errorCode } = error.response.data.validationErrors[0]

        this.alert.userData = this.handleError(errorCode)
      } finally {
        this.isLoading = false
      }
    },

    async submitPassword () {
      this.isLoading = true
      this.alert.password = null

      this.validateNewPassword()

      if (!this.passwordFormState.state) {
        this.isLoading = false
        return
      }

      try {
        const url = `${this.mainApi}auth/change_password`
        const payload = {
          old_password: this.form.password.current,
          new_password: this.form.password.new,
          password_confirmation: this.form.password.newConfirm
        }
        const response = await axios.post(url, payload)

        Object.assign(this.form.password, {
          current: '',
          new: '',
          newConfirm: ''
        })

        this.alert.password = {
          type: 'success',
          message: 'Senha alterada com sucesso!'
        }
      } catch (error) {
        const { data: errorCode } = error.response

        this.alert.password = this.handleError(errorCode)
      } finally {
        this.isLoading = false
      }
    },

    validateNewPassword() {
      const password = this.form.password.new;
      const fullName = this.form.userData.name;

      this.passwordFormState = {
        message: '',
        state: true
      }

      if (password === null) {
        this.passwordFormState.message = 'A senha não pode ser nula.';
        this.passwordFormState.state = false;
      }

      if (fullName === null) {
        this.passwordFormState.message = 'O nome completo não pode ser nulo.';
        this.passwordFormState.state = false;
      }

      const hasUppercase = /[A-Z]/.test(password);
      const hasLowercase = /[a-z]/.test(password);
      const hasNumber = /\d/.test(password);
      const hasSpecialChar = /[!@#$%^&*()_+~`|}{[\]:;?><,./-]/.test(password);
      const isLongEnough = password.length >= 12;
      const hasSequentialNumbers = /012|123|234|345|456|567|678|789|890|098|987|876|765|654|543|432|321|210/.test(password);
      const names = fullName.split(' ').filter(name => name.length > 2);
      const containsName = names.some(name => new RegExp(name, 'i').test(password));

      const blockedWords = ['adm', 'admin', 'mudar'];
      const foundBlockedWord = blockedWords.find(word => new RegExp(word, 'i').test(password));

      if (!hasUppercase) {
        this.passwordFormState.message = 'A senha deve conter pelo menos uma letra maiúscula.';
        this.passwordFormState.state = false;
      } else if (!hasLowercase) {
        this.passwordFormState.message = 'A senha deve conter pelo menos uma letra minúscula.';
        this.passwordFormState.state = false;
      } else if (!hasNumber) {
        this.passwordFormState.message = 'A senha deve conter pelo menos um número.';
        this.passwordFormState.state = false;
      } else if (!hasSpecialChar) {
        this.passwordFormState.message = 'A senha deve conter pelo menos um caractere especial.';
        this.passwordFormState.state = false;
      } else if (!isLongEnough) {
        this.passwordFormState.message = 'A senha deve conter pelo menos 12 caracteres.';
        this.passwordFormState.state = false;
      } else if (hasSequentialNumbers) {
        this.passwordFormState.message = 'A senha não pode conter sequências numéricas.';
        this.passwordFormState.state = false;
      } else if (containsName) {
        this.passwordFormState.message = 'A senha não pode conter o seu nome.';
        this.passwordFormState.state = false;
      } else if (foundBlockedWord) {
        this.passwordFormState.message = `A senha não pode conter a palavra '${foundBlockedWord}'.`;
        this.passwordFormState.state = false;
      } else {
        this.passwordFormState.state = true;
      }
    },

    handleError (errorCode) {
      return {
        type: 'danger',
        message: this.errorMessages[errorCode] || 'Ops! Algo deu errado... :('
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.account {
  $gap: map-get($layout-insights, 'gap');
  $border: 2px solid nth($colors-custom, 8);
  $border-radius: map-get($layout-insights, 'border-radius');

  border: $border;
  border-radius: $border-radius;
  background: #fff;

  &__tabs {
    &__index {
      z-index: 2;
      position: relative;

      &__tab {
        display: inline;

        a {
          display: inline-block;
          padding: 15px 40px;
          border: $border;
          border: {
            bottom: 0;
            color: transparent;
          }
          border-radius: $border-radius $border-radius 0 0;
          transform: translate(-2px, -2px);
          font: {
            weight: 700;
            size: 16px;
          }
          color: map-get($colors-util, 'muted');

          svg {
            width: 13px;
            margin-left: 2px;
            fill: map-get($colors-util, 'muted');
          }
        }

        &--on {
          a {
            border-color: nth($colors-custom, 8);
            background: #fff;
            color: nth($colors-custom, 1);

            svg {
              fill: nth($colors-custom, 1);
            }
          }
        }
      }
    }

    &__content {
      z-index: 1;
      position: relative;
      padding: map-get($layout-insights, 'gap');
      border-top: $border;
      transform: translateY(-4px);
    }
  }

  &__form {
    &--disabled {
      /deep/ {
        .form-field {
          @extend %disabled;
        }
      }
    }
  }
}

.error-message {
  color: red;
  font-size: 12px;
  margin-top: 5px;
}
</style>
